/**
 * Boli Live - Home Page
 *
 * eBay-style homepage with:
 * - Hero carousel
 * - Category cards
 * - Products from stores (main content)
 * - Live auctions section (secondary feature)
 */

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Radio,
  Smartphone,
  Monitor,
  Tablet,
  ChevronDown,
  Clock,
  TrendingUp,
  DollarSign,
  Grid,
  List,
  SlidersHorizontal,
} from 'lucide-react';
import { getBiddingService, getProductService } from '@boli/core';
import { AuctionCard, AuctionCardSkeleton } from '../components/AuctionCard';
import { ProductCard } from '../components/ProductCard';
import { HeroCarousel } from '../components/HeroCarousel';
import { CATEGORY_DATA } from '../lib/category-data';
import { cn } from '../lib/utils';
import type { Auction, Product } from '@boli/types';

// Category cards for homepage
const CATEGORY_CARDS = [
  {
    id: 'electronics',
    name: 'Electronics',
    subtitle: 'Phones, laptops & more',
    icon: <Smartphone className="w-8 h-8" />,
    bgColor: 'bg-blue-50',
    iconColor: 'text-blue-600',
  },
  {
    id: 'computers',
    name: 'Computers',
    subtitle: 'Desktops & accessories',
    icon: <Monitor className="w-8 h-8" />,
    bgColor: 'bg-purple-50',
    iconColor: 'text-purple-600',
  },
  {
    id: 'tablets',
    name: 'Tablets',
    subtitle: 'iPads & Android tablets',
    icon: <Tablet className="w-8 h-8" />,
    bgColor: 'bg-pink-50',
    iconColor: 'text-pink-600',
  },
  {
    id: 'live',
    name: 'Live Auctions',
    subtitle: 'Bid in real-time',
    icon: <Radio className="w-8 h-8" />,
    bgColor: 'bg-red-50',
    iconColor: 'text-red-600',
    isLive: true,
  },
];

// Sort options
const SORT_OPTIONS = [
  { id: 'newest', label: 'Newest First', icon: <Clock className="w-4 h-4" /> },
  { id: 'popular', label: 'Most Popular', icon: <TrendingUp className="w-4 h-4" /> },
  { id: 'price_low', label: 'Price: Low to High', icon: <DollarSign className="w-4 h-4" /> },
  { id: 'price_high', label: 'Price: High to Low', icon: <DollarSign className="w-4 h-4" /> },
];

// Fetch products from stores
async function fetchProducts(sort?: string): Promise<Product[]> {
  try {
    const productService = getProductService();
    // Map our sort options to API-compatible values
    const sortMap: Record<string, 'newest' | 'price_asc' | 'price_desc' | 'bestselling'> = {
      'newest': 'newest',
      'popular': 'bestselling',
      'price_low': 'price_asc',
      'price_high': 'price_desc',
    };
    const result = await productService.getProducts({
      status: 'active',
      sort: sortMap[sort || 'newest'] || 'newest',
      limit: 20,
    });
    return result.products;
  } catch (error) {
    console.error('Failed to fetch products:', error);
    return [];
  }
}

// Fetch live auctions
async function fetchLiveAuctions(): Promise<Auction[]> {
  try {
    const biddingService = getBiddingService();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const config = (biddingService as any).config;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const fetchAdapter = (biddingService as any).fetchAdapter;

    const response = await fetchAdapter(`${config.apiBaseUrl}/api/auctions?status=live&limit=8`);
    if (!response.ok) throw new Error('Failed to fetch auctions');

    const data = await response.json();
    return Array.isArray(data) ? data : (data.items || []);
  } catch (error) {
    console.error('Failed to fetch auctions:', error);
    return [];
  }
}

// Product card skeleton
function ProductCardSkeleton() {
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden animate-pulse">
      <div className="aspect-square bg-gray-200" />
      <div className="p-4 space-y-3">
        <div className="h-4 bg-gray-200 rounded w-3/4" />
        <div className="h-4 bg-gray-200 rounded w-1/2" />
        <div className="h-5 bg-gray-200 rounded w-1/3" />
      </div>
    </div>
  );
}

export default function HomePage() {
  const [selectedSort, setSelectedSort] = useState('newest');
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Fetch products
  const { data: products, isLoading: productsLoading } = useQuery<Product[]>({
    queryKey: ['products', 'homepage', selectedSort],
    queryFn: () => fetchProducts(selectedSort),
  });

  // Fetch live auctions
  const { data: liveAuctions, isLoading: auctionsLoading } = useQuery<Auction[]>({
    queryKey: ['auctions', 'live', 'homepage'],
    queryFn: fetchLiveAuctions,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const selectedSortLabel = SORT_OPTIONS.find(s => s.id === selectedSort)?.label || 'Sort';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Carousel */}
      <section className="max-w-7xl mx-auto px-4 py-6">
        <HeroCarousel />
      </section>

      {/* Category Cards */}
      <section className="max-w-7xl mx-auto px-4 pb-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {CATEGORY_CARDS.map((category) => (
            <Link
              key={category.id}
              to={category.id === 'live' ? '/?format=live' : `/category/${category.id}`}
              className={cn(
                'relative flex flex-col items-center justify-center p-6 rounded-xl transition-all hover:shadow-lg hover:-translate-y-1',
                category.bgColor
              )}
            >
              {category.isLive && (
                <span className="absolute top-3 right-3 flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                </span>
              )}
              <div className={cn('mb-3', category.iconColor)}>
                {category.icon}
              </div>
              <h3 className="font-semibold text-gray-900">{category.name}</h3>
              <p className="text-sm text-gray-600 text-center">{category.subtitle}</p>
              <ArrowRight className="w-4 h-4 mt-2 text-gray-400" />
            </Link>
          ))}
        </div>
      </section>

      {/* Live Auctions Section (if any) */}
      {liveAuctions && liveAuctions.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 pb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-gray-900">Live Now</h2>
              <span className="flex items-center gap-1.5 px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                </span>
                {liveAuctions.length} auctions
              </span>
            </div>
            <Link
              to="/?format=live"
              className="text-primary text-sm font-medium hover:underline flex items-center gap-1"
            >
              See all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {auctionsLoading ? (
              Array.from({ length: 4 }).map((_: unknown, i: number) => (
                <AuctionCardSkeleton key={i} />
              ))
            ) : (
              liveAuctions.slice(0, 4).map((auction) => (
                <AuctionCard
                  key={auction.id}
                  auction={{
                    ...auction,
                    sellerName: auction.sellerId ? 'Seller ' + auction.sellerId.substring(0, 4) : 'Top Seller',
                    sellerRating: 4.5 + (Math.random() * 0.5),
                    watcherCount: Math.floor(Math.random() * 20),
                  }}
                />
              ))
            )}
          </div>
        </section>
      )}

      {/* Main Products Section */}
      <section className="max-w-7xl mx-auto px-4 pb-12">
        {/* Section Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Daily Deals</h2>
            <p className="text-sm text-gray-600">Shop great deals from top sellers</p>
          </div>

          <div className="flex items-center gap-3">
            {/* View Toggle */}
            <div className="hidden md:flex items-center border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={cn(
                  'p-2 transition-colors',
                  viewMode === 'grid' ? 'bg-gray-100 text-primary' : 'text-gray-400 hover:text-gray-600'
                )}
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={cn(
                  'p-2 transition-colors',
                  viewMode === 'list' ? 'bg-gray-100 text-primary' : 'text-gray-400 hover:text-gray-600'
                )}
              >
                <List className="w-5 h-5" />
              </button>
            </div>

            {/* Sort Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsSortOpen(!isSortOpen)}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                <SlidersHorizontal className="w-4 h-4" />
                <span className="hidden sm:inline">{selectedSortLabel}</span>
                <ChevronDown className={cn("w-4 h-4 transition-transform", isSortOpen && "rotate-180")} />
              </button>

              {isSortOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setIsSortOpen(false)} />
                  <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
                    {SORT_OPTIONS.map((option) => (
                      <button
                        key={option.id}
                        onClick={() => {
                          setSelectedSort(option.id);
                          setIsSortOpen(false);
                        }}
                        className={cn(
                          'w-full flex items-center gap-2 px-4 py-2 text-sm text-left hover:bg-gray-50 transition-colors',
                          selectedSort === option.id && 'bg-primary/10 text-primary font-medium'
                        )}
                      >
                        {option.icon}
                        {option.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Products Grid */}
        <AnimatePresence mode="wait">
          {productsLoading ? (
            <div className={cn(
              'grid gap-4',
              viewMode === 'grid'
                ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5'
                : 'grid-cols-1'
            )}>
              {Array.from({ length: 10 }).map((_: unknown, i: number) => (
                <ProductCardSkeleton key={i} />
              ))}
            </div>
          ) : products && products.length > 0 ? (
            <motion.div
              className={cn(
                'grid gap-4',
                viewMode === 'grid'
                  ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5'
                  : 'grid-cols-1'
              )}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              {products.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.03 }}
                >
                  <ProductCard product={product} viewMode={viewMode} />
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <p className="text-gray-500 mb-4">No products found. Be the first to list!</p>
              <Link
                to="/seller/products/new"
                className="inline-flex items-center gap-2 px-6 py-2 bg-primary text-white font-medium rounded-full hover:bg-primary/90 transition-colors"
              >
                Start Selling
              </Link>
            </div>
          )}
        </AnimatePresence>

        {/* Load More Button */}
        {products && products.length > 0 && (
          <div className="text-center mt-8">
            <button className="px-8 py-3 bg-white border border-gray-300 text-gray-700 font-medium rounded-full hover:bg-gray-50 transition-colors">
              Show more
            </button>
          </div>
        )}
      </section>

      {/* Browse by Category Section */}
      <section className="bg-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Explore Popular Categories</h2>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {Object.values(CATEGORY_DATA).map((category) => (
              <Link
                key={category.id}
                to={`/category/${category.slug}`}
                className="group flex flex-col items-center p-4 rounded-xl hover:bg-gray-50 transition-colors"
              >
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-3 group-hover:bg-primary/10 transition-colors">
                  <span className="text-2xl">
                    {category.id === 'electronics' && '📱'}
                    {category.id === 'fashion' && '👕'}
                    {category.id === 'home' && '🏠'}
                    {category.id === 'collectibles' && '🎨'}
                    {category.id === 'motors' && '🚗'}
                    {category.id === 'sports' && '⚽'}
                  </span>
                </div>
                <span className="text-sm font-medium text-gray-900 text-center group-hover:text-primary transition-colors">
                  {category.name}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Sell CTA Section */}
      <section className="max-w-7xl mx-auto px-4 py-12">
        <div className="bg-gradient-to-r from-primary to-blue-700 rounded-2xl p-8 md:p-12 text-white text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Start selling on Boli today
          </h2>
          <p className="text-white/90 mb-6 max-w-xl mx-auto">
            List your items for free and reach millions of buyers. Go live to auction your products in real-time!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/seller/products/new"
              className="inline-flex items-center justify-center px-8 py-3 bg-white text-primary font-semibold rounded-full hover:bg-gray-100 transition-colors"
            >
              List an item
            </Link>
            <Link
              to="/go-live"
              className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-white/20 text-white font-semibold rounded-full hover:bg-white/30 transition-colors"
            >
              <Radio className="w-5 h-5" />
              Go Live
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
